<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <h2 class="large mb-8 mt-4">Requesting practitioner details</h2>
    <div class="grid-container mb-10" id="requesting-practitioner-grid">
      <v-alert
        v-if="error !== null"
        class="top-spaced-item multi-line custom-alert mb-6"
        type="error"
      >
        <v-icon left color="#BF0000">mdi-alert-circle</v-icon>
        {{ error }}
      </v-alert>
      <v-row>
        <v-col class="custom-col col-2">
          <span class="text-title">Title</span>
          <v-select
            v-model="namePrefix"
            id="practitioner.namePrefix"
            :items="titleList"
            item-text="displayText"
            item-value="value"
            outlined
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col class="custom-col">
          <span class="text-title">Given name*</span>
          <v-text-field
            v-model="firstName"
            id="practitioner.firstName"
            :rules="ruleNotNull"
            outlined
            :maxlength="limitGivenName"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col class="custom-col">
          <span class="text-title">Surname*</span>
          <v-text-field
            v-model="lastName"
            outlined
            id="practitioner.lastName"
            :rules="ruleNotNull"
            :maxlength="limitSurname"
            hide-details="auto"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="custom-col col-3">
          <span class="text-title">Provider number</span>
          <v-text-field
            v-model="providerNum"
            id="practitioner.providerNum"
            :rules="providerNum !== '' ? ruleProviderNumber : []"
            class="shrink"
            outlined
            :maxlength="limitProviderNum"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col class="custom-col">
          <span class="text-title">Clinic name*</span>
          <v-text-field
            v-model="clinicName"
            id="practitioner.clinicName"
            :rules="ruleNotNull"
            class="shrink"
            outlined
            :maxlength="limitClinicName"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col class="custom-col">
          <span class="text-title">Department/Suite</span>
          <v-text-field
            v-model="dept"
            id="practitioner.dept"
            outlined
            :maxlength="limitDeptSuite"
            hide-details="auto"
          ></v-text-field>
        </v-col>
      </v-row>
      <AddressSection
        :loadFromStorage="loadedFromStorage"
        :reuseDataObj="reusePractitionerData"
        ref="practitioner_address"
        addressRef="practitioner"
        class="address-class"
        id="practitioner-address"
        idPrefix="practitioner."
        filled
      ></AddressSection>
      <v-row class="mt-4">
        <v-col class="custom-col">
          <span class="text-title">Phone/Mobile*</span>
          <v-text-field
            v-model="number"
            id="practitioner.number"
            outlined
            :rules="number !== '' ? ruleValidNumber : ruleNotNull"
            :maxlength="limitPhone"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col class="custom-col">
          <span class="text-title">Email*</span>
          <v-text-field
            v-model="emailAddress"
            id="practitioner.emailAddress"
            :rules="ruleValidEmail"
            outlined
            :maxlength="limitEmail"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col class="custom-col">
          <span class="text-title">Fax</span>
          <v-text-field
            v-model="faxNum"
            id="practitioner.faxNum"
            outlined
            :rules="faxNum !== '' ? ruleValidNumber : []"
            :maxlength="limitFax"
            hide-details="auto"
          ></v-text-field>
        </v-col>
      </v-row>
    </div>
    <p v-if="showBookmark" style="grid-area: bookmarkGPData">
      This is your
      <a id="bookmarkGPLink" :href="link" target="_blank"
        >requesting practitioner details</a
      >, save or bookmark it for later use.
    </p>
    <div class="mb-4">
      <v-btn
        class="primary-btn btn-padding-medium mb-2"
        max-width="350"
        @click="
          saveComponentData('practitioner', transferData());
          loadedFromStorage = true;
        "
        >Save practitioner details</v-btn
      >
      <v-btn
        v-if="loadedFromStorage"
        max-width="200"
        @click="
          clearComponentData('practitioner');
          loadedFromStorage = false;
          providerNum = '';
        "
        class="secondary-btn btn-padding-medium left-spaced-item mb-2"
        >Clear details</v-btn
      >
      <p>Save the details to this computer for future orders.</p>
    </div>
    <div class="vertical">
      <v-btn
        titlecase
        class="primary-btn btn-padding-medium mb-2 mt-2"
        max-width="350"
        @click="
          link = generateGPBookmark(transferData());
          showBookmark = true;
        "
        >Generate bookmark URL</v-btn
      >
      <p>
        Generate a bookmark link for use on any computer. Bookmark or email it
        to yourself for later use.
      </p>
    </div>
    <div id="copy-to-grid">
      <div v-if="copyToIds.length > 0">
        <h2 class="mb-4 mt-10">Copy to</h2>
      </div>
      <div class="grid-container mt-4">
        <div v-if="copyToIds.length > 0">
          <div v-for="i in copyToIds" v-bind:key="i">
            <CopyTo
              :doctor="
                copyToDoctors.length === 0
                  ? {}
                  : copyToDoctors[copyToIds.indexOf(i)]
              "
              ref="copyTo"
              :currIndex="i"
              :currCount="copyToIds.indexOf(i).toString()"
              @removeCopyTo="removeCopyTo"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <v-btn
        @click="generateCopyTo()"
        class="primary-btn btn-padding-medium mb-2"
        id="copy-to-add-button"
        >Add a CC doctor</v-btn
      >
    </div>
  </v-form>
</template>

<script>
import CopyTo from "./CopyTo";
import FormRulesMixin from "../mixins/FormRulesMixin";
import DropDownMixin from "../mixins/DropDownMixin";
import StorageMixin from "../mixins/StorageMixin";
import UtiliseMixin from "../mixins/UtiliseMixin";
import { useSectionStore } from "../store/SectionStore";
import AddressSection from "./AddressSection.vue";

export default {
  name: "",
  props: {
    reusePractitionerData: Object,
    error: String,
  },
  components: {
    CopyTo,
    AddressSection,
  },
  async mounted() {
    // Custom handling of loading practitioner data from querystring if present
    // Assume they will use the display-text as the key
    if (window.location.search) {
      let valueToDisplayTextMapping = await this.getDropDownMapping(
        false,
        true
      );

      // Set which keys are allowed and which are from a Dropdown select
      let allowedDataKeysSelect = {
        state: true,
        country: true,
        lastName: false,
        namePrefix: false,
        firstName: false,
        providerNum: false,
        dept: false,
        unitFlat: false,
        streetAddress: false,
        clinicName: false,
        city: false,
        postcode: false,
        emailAddress: false,
        number: false,
        faxNum: false,
      };
      let urlSearchParams = new URLSearchParams(window.location.search);

      // for non-production there are special keys that help us prefill the form (NOTE: this is not the technique
      // that should be used for actual automated testing i.e. cypress - this is to help out with ad-hoc manual
      // testing)
      urlSearchParams.forEach((value, key) => {
        if (key === "testPractitioner") {
          if (value === "nick" || value == "") {
            this["namePrefix"] = "DR";
            this["firstName"] = "Test";
            this["lastName"] = "Test";
            this["providerNum"] = "2222222B";
            this["unitFlat"] = "Lvl 2";
            this["streetAddress"] = "454 Main Street";
            this["clinicName"] = "Upstairs Hollywood Medical Clinical";
            this["dept"] = "Genetics Dept";
            this["city"] = "Springfield";
            this["state"] = "";
            this["country"] = "NZL";
            this["postcode"] = "3001";
            this["emailAddress"] = "drnick@hotmail.com";
            this["number"] = "0432143213";
          }
          if (value === "julius") {
            this["namePrefix"] = "DR";
            this["firstName"] = "Julius";
            this["lastName"] = "Hibbert";
            this["providerNum"] = "1234567A";
            this["dept"] = "Pediatrics";
            this["clinicName"] = "Springfield General Hospital";
            this["city"] = "Springfield";
            this["state"] = "QLD";
            this["postcode"] = "7000";
            this["emailAddress"] = "julius@hotmail.com";
          }
        }
      });

      for (let [key, isSelect] of Object.entries(allowedDataKeysSelect)) {
        let val = urlSearchParams.get(key);
        if (val) {
          if (isSelect) {
            this[key] = valueToDisplayTextMapping[val.toLowerCase()];
          } else {
            this[key] = val;
          }
        }
      }
    } else {
      // Storage enabled so let's load the data if we can
      this.loadedFromStorage = this.loadComponentData("practitioner");
    }
  },
  mixins: [FormRulesMixin, DropDownMixin, StorageMixin, UtiliseMixin],
  data: function() {
    let copyToDoctorsIds = [];
    if (this.reusePractitionerData.copyToDoctors != null) {
      this.reusePractitionerData.copyToDoctors.forEach(() => {
        copyToDoctorsIds.push(this.generateId());
      });
    }
    var newData = {
      loadedFromStorage: false,
      copyToIds: copyToDoctorsIds,
      country: this.reusePractitionerData.country || "AUS",
      state: this.reusePractitionerData.state || "",
      providerNum: this.reusePractitionerData.providerNum || "",
      lastName: this.reusePractitionerData.lastName || "",
      firstName: this.reusePractitionerData.firstName || "",
      namePrefix: this.reusePractitionerData.namePrefix || "",
      clinicName: this.reusePractitionerData.clinicName || "",
      dept: this.reusePractitionerData.dept || "",
      unitFlat: this.reusePractitionerData.unitFlat || "",
      streetAddress: this.reusePractitionerData.streetAddress || "",
      city: this.reusePractitionerData.city || "",
      postcode: this.reusePractitionerData.postcode || "",
      emailAddress: this.reusePractitionerData.emailAddress || "",
      number: this.reusePractitionerData.number || "",
      faxNum: this.reusePractitionerData.faxNum || "",
      showBookmark: false,
      link: window.location.hostname,
      copyToDoctors: this.reusePractitionerData.copyToDoctors || [],
    };
    return newData;
  },
  methods: {
    removeCopyTo(value) {
      let idxToRemove = this.copyToIds.indexOf(value);
      this.copyToIds.splice(idxToRemove, 1);
      this.copyToDoctors.splice(idxToRemove, 1);
    },
    generateId: function() {
      return Math.random()
        .toString(36)
        .substr(2, 9);
    },
    generateCopyTo: function() {
      if (this.copyToIds && this.copyToIds.length < 4) {
        this.copyToIds.push(this.generateId());
      }
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      setTimeout(() => {
        const currIndex = state.testOrderDom.indexOf("practitioner_section");
        this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
      }, 500);
      state.nextPanel();
    },
    isFormValid() {
      return this.$refs.practitioner_address.validateForm();
    },
    validatedIndividualCcDr(ccDrData) {
      /** firstName, lastName and clinicName related fields are all required fields.
       * Therefore, if end users adds a cc dr in the Copy to doctor section,
       * those three fields' values should never be empty string.
       * When their values are empty strings, it is a result of the || "" we added in
       * `transferData` method in CopyTo.vue component.
       * When any of the three fields has empty string value, we can consider the cc dr element
       * is invalid and will not be pushed to `copyToDoctors` array
       */
      if (
        ccDrData.firstName === "" ||
        ccDrData.lastName === "" ||
        ccDrData.clinicName === ""
      )
        return false;

      return true;
    },
    transferData() {
      let copyToDoctors = [];
      if ("copyTo" in this.$refs) {
        this.$refs.copyTo.forEach((component) => {
          const transferredData = component.transferData();

          if (this.validatedIndividualCcDr(transferredData)) {
            copyToDoctors.push(transferredData);
          }
        });
      }
      const addressData = this.$refs.practitioner_address.transferData();

      let dataObj = {
        providerNum: this.providerNum,
        lastName: this.lastName,
        firstName: this.firstName,
        namePrefix: this.namePrefix,
        clinicName: this.clinicName,
        dept: this.dept,
        unitFlat: addressData.unitFlat,
        streetAddress: addressData.streetAddress,
        city: addressData.city,
        state: addressData.state,
        postcode: addressData.postcode,
        country: addressData.country,
        emailAddress: this.emailAddress,
        number: this.cleanPhoneNumber(this.number),
        faxNum: this.cleanPhoneNumber(this.faxNum),
        copyToDoctors,
      };
      return dataObj;
    },
  },
};
</script>
